'use client';

import type { MissionsGroupVariants } from '../../types';
import type { MissionTargetActions } from './utils/helpers';
import type { UserQuestDto } from '@/queries/userQuest/userQuest.schema';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { GmMissionCard } from '@/components/entities/gamification/GmMissionCard';
import { GmMissionsSkeleton } from '@/components/entities/gamification/skeletons/GmMissionsSkeleton';
import { Icon } from '@/components/entities/Sprite';
import { useSamePageScrollTopAction } from '@/hooks/useSamePageScrollTopAction';
import { useTimer } from '@/hooks/useTimer';
import { cn } from '@/utils/cn';
import { MissionsGroupEmptyState } from './components/MissionGroupEmptyState';
import { areAllWelcomeMissionsCompleted } from './components/welcomeMission.utils';
import { WelcomeMissions } from './components/WelcomeMissions';
import { formatTimeLeft, getMissionAction, getMissionHref, MISSION_LINK_TARGET_BY_TYPE } from './utils/helpers';

interface Props {
  variant: MissionsGroupVariants;
  missions?: UserQuestDto[];
  loading?: boolean;
  onExpired?: VoidFunction;
  nextUpdate?: number | Date;
  targetActions?: MissionTargetActions;
}

export const MissionsGroup = ({ loading, onExpired, variant, missions = [], nextUpdate, targetActions }: Props) => {
  const t = useTranslations('gamification');
  const getSamePageScrollTopAction = useSamePageScrollTopAction();
  const expiredTargetTimestampRef = useRef<number | null>(null);
  const [updateTime, setUpdateTime] = useState<Date | null>(null);

  const targetTimestamp = typeof nextUpdate === 'number' ? nextUpdate : (nextUpdate?.getTime() ?? null);
  const { timeComponents, isExpired } = useTimer({ targetDate: updateTime });

  useEffect(() => {
    if (targetTimestamp === null) {
      expiredTargetTimestampRef.current = null;
      setUpdateTime(null);
      return;
    }

    if (expiredTargetTimestampRef.current === targetTimestamp) {
      return;
    }

    setUpdateTime(new Date(targetTimestamp));
  }, [targetTimestamp]);

  useEffect(() => {
    if (isExpired && updateTime) {
      expiredTargetTimestampRef.current = updateTime.getTime();
      setUpdateTime(null);
      onExpired?.();
    }
  }, [isExpired, onExpired, updateTime]);

  const timeLeft = formatTimeLeft(timeComponents);
  const title = t(`mission-group.${variant}-missions.title`);

  const isWelcomeMissions = variant === 'welcome';
  const hasNoActiveMissions = !missions.length || missions.every((mission) => {
    const done = mission.progress ?? 0;
    const total = mission.quest.progress.target ?? 0;
    return done === total;
  });

  const renderMissions = () => {
    if (isWelcomeMissions) {
      return <WelcomeMissions missions={missions} />;
    }

    if (hasNoActiveMissions) {
      return <MissionsGroupEmptyState variant={variant} />;
    }

    return missions.map((task) => {
      const target = MISSION_LINK_TARGET_BY_TYPE[task.quest.questTemplate.type];
      const href = getMissionHref(task);
      const overrideAction = (target && targetActions?.[target]) ?? getSamePageScrollTopAction(href);

      return (
        <GmMissionCard
          key={task.id}
          {...task}
          onAction={overrideAction ?? getMissionAction(task)}
          href={overrideAction ? undefined : href}
        />
      );
    });
  };

  if (loading) {
    return <GmMissionsSkeleton variant={variant} />;
  }

  if (isWelcomeMissions && (missions.length === 0 || areAllWelcomeMissionsCompleted(missions))) {
    return null;
  }

  return (
    <div className="flex flex-col w-full gap-3">
      <div className="flex justify-between md:justify-start gap-3 items-center">
        <p className="lg:leading-5.5 text-sm leading-5 lg:text-lg font-medium text-light text-start">
          {title}
        </p>
        {
          nextUpdate && timeLeft && (
            <div className="flex text-main-pending justify-center items-center gap-1">
              <Icon name="clock" className="size-4" />
              <p className="text-sm leading-5">{`${t('mission-group.next-in')} ${timeLeft}`}</p>
            </div>
          )
        }
      </div>

      <div className={cn('lg:grid flex flex-col gap-2 lg:gap-3', hasNoActiveMissions || isWelcomeMissions ? 'lg:grid-cols-1' : 'lg:grid-cols-3')}>
        {renderMissions()}
      </div>
    </div>
  );
};
